import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  ArrowBack as ArrowBackIcon,
  Badge as BadgeIcon,
  TrendingUp,
  EventNote,
  PaidRounded
} from '@mui/icons-material';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { supabase } from '../../supabaseClient';
import { useOffice } from '../../store/officeContext';
import CaseCard from '../../components/Card/Case/CaseCard';
import CaseTable from '../../components/Card/Case/CaseTable';
import FilterChip from '../../components/ui/FilterChip';
import { tokens } from '../../theme/tokens';
import { useCases } from '../../store/casesContext';
import { useCachedInsight } from '../../hooks/useCachedInsight';
import { askOfficeCopilot } from '../../hooks/useOfficeCopilot';
import AiInsightCard from '../../components/Copilot/AiInsightCard';

const CASE_TABS = [
  { value: 'activos', label: 'Activos' },
  { value: 'finalizados', label: 'Finalizados' },
  { value: 'todos', label: 'Todos' },
];

// ---------- helpers de nombre (full_name = "nombres;apellidos") ----------
const getFirstName = (fullName) => {
  if (!fullName) return '';
  const [names] = fullName.split(';');
  return names?.trim().split(' ')[0] || '';
};

const getDisplayName = (fullName) => {
  if (!fullName) return 'Sin asignar';
  const [names, surnames] = fullName.split(';').map(s => s?.trim() ?? '');
  const firstName = names?.split(' ')[0] ?? '';
  const firstSurname = surnames?.split(' ')[0] ?? '';
  return `${firstName} ${firstSurname}`.trim() || 'Sin asignar';
};

const currencyFormatter = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const formatGreetingDate = () => {
  const str = new Date().toLocaleDateString('es-ES', {
    weekday: 'long', day: 'numeric', month: 'long'
  });
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// ---------- estilos compartidos ----------
const cardSx = {
  background: tokens.colors.panel,
  border: `1px solid ${tokens.colors.line}`,
  borderRadius: `${tokens.radius.lg}px`,
  boxShadow: tokens.shadow.card,
  p: 2.75,
};

const ghostBtnSx = {
  borderRadius: `${tokens.radius.md}px`,
  borderColor: tokens.colors.line2,
  color: tokens.colors.txt,
  fontWeight: 650,
  textTransform: 'none',
  '&:hover': { borderColor: tokens.colors.line2, backgroundColor: tokens.colors.panel2 },
};

const primaryBtnSx = {
  borderRadius: `${tokens.radius.md}px`,
  fontWeight: 650,
  textTransform: 'none',
  backgroundImage: tokens.gradient.main,
  color: '#08131a',
  boxShadow: tokens.shadow.gradientButton,
  '&:hover': { backgroundImage: tokens.gradient.main, filter: 'brightness(1.06)' },
};

// ---------- KPI card ----------
const KpiCard = ({ icon, label, value, sub }) => (
  <Box sx={{ ...cardSx, width: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: tokens.colors.muted, fontSize: 12.5, fontWeight: 600 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: `${tokens.radius.sm}px`,
        display: 'grid', placeItems: 'center',
        backgroundImage: tokens.gradient.soft, color: tokens.colors.txt
      }}>
        {icon}
      </Box>
      {label}
    </Box>
    <Typography sx={{ fontSize: 30, fontWeight: 760, letterSpacing: '-1px', mt: 1.75, mb: 0.5 }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: 12.5, color: tokens.colors.muted, fontWeight: 550, mt: 'auto' }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const OfficeHomeView = () => {
  const office = useOffice().office;
  const navigate = useNavigate();
  const { setCases } = useCases();

  const [expedients, setExpedients] = useState([]);
  const [filteredExpedients, setFilteredExpedients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('');
  const [caseTab, setCaseTab] = useState('todos');
  const [userRole, setUserRole] = useState(null);
  const [currentOffice, setCurrentOffice] = useState(null);
  const [officeId, setOfficeId] = useState(null);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table');

  const [practiceAreas, setPracticeAreas] = useState([]);
  const [loadingPracticeAreas, setLoadingPracticeAreas] = useState(false);

  // ---------- fetch helpers ----------
  const fetchCurrentUser = async (officeId) => {
    try {
      setUserLoading(true);
      setError(null);
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) { setError('Error de autenticación'); return; }
      if (!user) { setError('Usuario no autenticado'); navigate('/login'); return; }

      const { data: personData, error: personError } = await supabase
        .from('person').select('*').eq('id', user.id).single();
      if (personError || !personData) { setError('Error al cargar los datos del usuario'); return; }

      const { data: memberData, error: memberError } = await supabase
        .from('office_member').select('role')
        .eq('member_id', user.id).eq('office_id', officeId).single();
      if (memberError || !memberData) { setError('No tienes permisos para acceder a este estudio jurídico'); return; }

      setCurrentUser({ id: user.id, name: personData.full_name, email: user.email, ...personData });
      setUserRole(memberData.role);
    } catch (err) {
      console.error(err);
      setError('Error de conexión al cargar datos del usuario');
    } finally {
      setUserLoading(false);
    }
  };

  const fetchOfficeData = async (id) => {
    try {
      const { data, error } = await supabase.from('office').select('*').eq('id', id).single();
      if (error || !data) { setError('Error al cargar los datos del estudio jurídico'); return; }
      setCurrentOffice(data);
    } catch (err) {
      console.error(err);
      setError('Error de conexión al cargar el estudio jurídico');
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await fetchCurrentUser(office.id);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };
    initializeData();
  }, []);

  // ---------- filtrado ----------
  useEffect(() => {
    if (loading) return;
    if (expedients.length > 0) setFilterLoading(true);

    const timeoutId = setTimeout(() => {
      let filtered = expedients;

      if (caseTab === 'activos') filtered = filtered.filter(exp => exp.state !== 'completed');
      if (caseTab === 'finalizados') filtered = filtered.filter(exp => exp.state === 'completed');

      if (selectedType) {
        filtered = filtered.filter(exp => exp.office_practice_area_id === selectedType);
      }

      setFilteredExpedients(filtered);
      setFilterLoading(false);
    }, expedients.length > 100 ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [expedients, caseTab, selectedType, loading]);

  const getRoleLabel = (role) => ({
    admin: 'Administrador', partner: 'Colaborador', lawyer: 'Abogado', client: 'Cliente'
  }[role] || role);

  const loadPracticeAreas = async () => {
    try {
      setLoadingPracticeAreas(true);
      const { data, error } = await supabase
        .from('office_practice_area')
        .select(`id, practice_area:practice_area_id ( id, name )`)
        .eq('office_id', office.id);
      if (error) return;
      setPracticeAreas((data || []).map(item => ({ id: item.id, name: item.practice_area.name })));
    } finally {
      setLoadingPracticeAreas(false);
    }
  };

  const loadExpedients = async () => {
    try {
      if (!currentUser) return;
      setLoading(true);

      const { data: casesData, error } = await supabase
        .from('case').select('*').eq('office_id', office.id).neq('state', 'completed');
      if (error) throw error;

      const caseIds = casesData.map(c => c.id);
      const { data: allCaseMembers } = await supabase
        .from('case_member')
        .select(`case_id, role, person_id, name_str, person:person_id ( id, full_name, email, document_number, phone_number )`)
        .in('case_id', caseIds);

      const membersByCase = {};
      (allCaseMembers || []).forEach(m => {
        (membersByCase[m.case_id] ||= []).push(m);
      });

      let filteredCases = casesData;
      if (userRole !== 'admin') {
        const userCaseIds = new Set();
        (allCaseMembers || []).forEach(m => {
          if (m.person_id === currentUser.id || m.name_str?.toLowerCase().includes(currentUser.email?.toLowerCase())) {
            userCaseIds.add(m.case_id);
          }
        });
        filteredCases = casesData.filter(c => userCaseIds.has(c.id))
          .filter(c => c.is_visible && c.state !== 'draft');
      }

      const filteredCaseIds = filteredCases.map(c => c.id);
      const { data: allTasks } = await supabase.from('task').select('*').in('case_id', filteredCaseIds);
      const tasksByCase = {};
      (allTasks || []).forEach(t => { (tasksByCase[t.case_id] ||= []).push(t); });

      const enrichedData = filteredCases.map(caseItem => {
        const caseMembers = membersByCase[caseItem.id] || [];
        const members = {
          client: caseMembers.find(m => m.role === 'client'),
          lawyer: caseMembers.find(m => m.role === 'lawyer'),
          partners: caseMembers.filter(m => m.role === 'partner')
        };
        const tasks = tasksByCase[caseItem.id] || [];
        const progress = tasks.length > 0
          ? Math.round((tasks.filter(t => t.completed).length / tasks.length) * 100)
          : 0;

        let userRoleInCase = null;
        if (userRole !== 'admin') {
          const userMember = caseMembers.find(m =>
            m.person_id === currentUser.id || m.name_str?.toLowerCase().includes(currentUser.email?.toLowerCase()));
          userRoleInCase = userMember?.role;
        }

        return { ...caseItem, case_members: caseMembers, members, tasks, progress, userRoleInCase };
      });

      setExpedients(enrichedData);
      setCases(enrichedData);
    } catch (err) {
      console.error(err);
      setError('Error de conexión al cargar los expedientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && userRole) {
      loadExpedients();
      loadPracticeAreas();
    }
  }, [currentUser, userRole]);

  const handleCreateExpedient = () => navigate(`/office/${office.id}/case/new`);
  const handleBackToOffices = () => navigate('/offices');
  const handleRetryFetch = () => {
    if (officeId) { fetchOfficeData(officeId); fetchCurrentUser(officeId); }
  };

  const isInitialLoading = loading || userLoading || !currentUser || !userRole;

  // ---------- datos derivados para el panel ----------
  const totalTasks = expedients.reduce((acc, e) => acc + (e.tasks?.length || 0), 0);
  const doneTasks = expedients.reduce((acc, e) => acc + (e.tasks?.filter(t => t.completed).length || 0), 0);
  const taskCompletionPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
  const totalBilled = expedients.reduce((acc, e) => acc + (e.billed_amount || 0), 0);

  const upcomingDeadlines = expedients
    .filter(e => e.end_date)
    .filter(e => {
      const days = (new Date(e.end_date) - new Date()) / 86400000;
      return days >= 0 && days <= 14;
    })
    .sort((a, b) => new Date(a.end_date) - new Date(b.end_date))
    .slice(0, 5);

  const weeklyDeadlines = expedients
    .filter(e => e.end_date)
    .filter(e => {
      const days = (new Date(e.end_date) - new Date()) / 86400000;
      return days >= 0 && days <= 7;
    });

  // carga por miembro (abogado responsable)
  const workloadMap = {};
  expedients.forEach(e => {
    const name = getDisplayName(e.members.lawyer?.person?.full_name);
    workloadMap[name] = (workloadMap[name] || 0) + 1;
  });
  const workload = Object.entries(workloadMap).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxWorkload = Math.max(1, ...workload.map(([, v]) => v));

  const insight = useCachedInsight(
    office?.id ? `insight:office:${office.id}` : null,
    () => askOfficeCopilot(office.id, 'Dame un resumen ejecutivo breve (máximo 3 líneas) de la situación actual del despacho: plazos próximos y expedientes que requieren atención.'),
    !isInitialLoading
  );

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 4, borderRadius: '14px' }}>
          <Typography variant="h6" gutterBottom>Error: {error}</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            No se pudo cargar la información del estudio jurídico o del usuario.
          </Typography>
          <Box display="flex" gap={2}>
            <Button variant="contained" startIcon={<ArrowBackIcon />} sx={primaryBtnSx} onClick={handleBackToOffices}>
              Volver a mis Despachos
            </Button>
            <Button variant="outlined" sx={ghostBtnSx} onClick={handleRetryFetch}>Reintentar</Button>
          </Box>
        </Alert>
      </Container>
    );
  }

  if (isInitialLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" width="100%" height={90} sx={{ mb: 2, borderRadius: '18px' }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3].map(i => (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Skeleton variant="rectangular" width="100%" height={110} sx={{ borderRadius: '18px' }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" width="100%" height={200} sx={{ mb: 3, borderRadius: '18px' }} />
        <Grid container spacing={3}>
          {[1, 2, 3].map(i => (
            <Grid item size={{ xs: 12, md: 4 }} key={i}>
              <Skeleton variant="rectangular" width="100%" height={260} sx={{ borderRadius: '18px' }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
        <>
          {/* ---------- Encabezado del Panel ---------- */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
            <Box>
              <Typography sx={{ fontSize: 26, fontWeight: 730, letterSpacing: '-.4px' }}>
                Buenos días, {getFirstName(currentUser?.name)}
              </Typography>
              <Typography sx={{ color: tokens.colors.muted, mt: 0.5, fontSize: 14 }}>
                {formatGreetingDate()} · {expedients.length} expedientes activos · {weeklyDeadlines.length} plazos esta semana
              </Typography>
            </Box>

            {userRole === 'admin' && (
              <Box display="flex" flexWrap="wrap" gap={1.5}>
                <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx} onClick={handleCreateExpedient}>
                  Nuevo expediente
                </Button>
                <Button
                  variant="outlined" startIcon={<EditIcon />} sx={ghostBtnSx}
                  onClick={() => navigate(`/office/${office.id}/settings`, { state: { office: currentOffice } })}
                >
                  Editar despacho
                </Button>
              </Box>
            )}
          </Box>

          <AiInsightCard loading={insight.loading} text={insight.text} error={insight.error} />

          {/* ---------- KPIs ---------- */}
          <Grid container spacing={2} sx={{ mb: 2, alignItems: 'stretch' }}>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
              <KpiCard icon={<AssignmentIcon fontSize="small" />} label="Expedientes activos" value={expedients.length} />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
              <KpiCard
                icon={<EventNote fontSize="small" />}
                label="Plazos próximos 7 días"
                value={weeklyDeadlines.length}
                sub={weeklyDeadlines.length > 0 ? `Próximo: ${new Date(weeklyDeadlines[0].end_date).toLocaleDateString('es-ES')}` : 'Sin plazos próximos'}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
              <KpiCard
                icon={<PaidRounded fontSize="small" />}
                label="Facturación en curso"
                value={currencyFormatter.format(totalBilled)}
                sub={`${expedients.length} expedientes activos`}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
              <KpiCard icon={<TrendingUp fontSize="small" />} label="Tareas completadas" value={`${taskCompletionPct}%`} sub={`${doneTasks}/${totalTasks} tareas`} />
            </Grid>
          </Grid>

          {/* ---------- Deadlines + Carga por miembro ---------- */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item size={{ xs: 12, md: 7 }}>
              <Box sx={cardSx}>
                <Typography sx={{ fontSize: 15, fontWeight: 680 }}>Expedientes con plazo próximo</Typography>
                <Typography sx={{ fontSize: 12.5, color: tokens.colors.muted, mb: 2 }}>Ordenados por urgencia</Typography>
                {upcomingDeadlines.length === 0 ? (
                  <Typography sx={{ color: tokens.colors.muted, fontSize: 13, py: 2, textAlign: 'center' }}>
                    No hay plazos en los próximos 14 días.
                  </Typography>
                ) : upcomingDeadlines.map(e => {
                  const days = Math.round((new Date(e.end_date) - new Date()) / 86400000);
                  const urgency = days <= 3 ? 'red' : days <= 7 ? 'warn' : 'ok';
                  const colorMap = { red: tokens.colors.red, warn: tokens.colors.gold, ok: tokens.colors.ok };
                  const bgMap = { red: 'rgba(255,107,107,.14)', warn: 'rgba(229,181,103,.16)', ok: 'rgba(74,217,145,.14)' };
                  return (
                    <Box
                      key={e.id}
                      onClick={() => navigate(`/office/${office.id}/case/${e.id}`)}
                      sx={{ display: 'flex', gap: 1.5, py: 1.5, borderBottom: `1px solid ${tokens.colors.line}`, cursor: 'pointer' }}
                    >
                      <Box sx={{ flex: '0 0 52px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 19, fontWeight: 730, lineHeight: 1 }}>
                          {new Date(e.end_date).getDate()}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: tokens.colors.muted, textTransform: 'uppercase' }}>
                          {new Date(e.end_date).toLocaleDateString('es-ES', { month: 'short' })}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 13.5 }} noWrap>{e.title}</Typography>
                        <Typography sx={{ color: tokens.colors.muted, fontSize: 12, mt: 0.25 }} noWrap>
                          {e.number} · {e.members.client?.person?.full_name || e.members.client?.name_str || 'Sin cliente'}
                        </Typography>
                      </Box>
                      <Chip
                        label={days <= 3 ? 'Crítico' : days <= 7 ? 'Próximo' : 'A tiempo'}
                        size="small"
                        sx={{ alignSelf: 'center', backgroundColor: bgMap[urgency], color: colorMap[urgency], fontWeight: 700, fontSize: 11 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            <Grid item size={{ xs: 12, md: 5 }}>
              <Box sx={{ ...cardSx, mb: 2 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 680 }}>Carga por miembro</Typography>
                <Typography sx={{ fontSize: 12.5, color: tokens.colors.muted, mb: 2 }}>Expedientes activos asignados</Typography>
                {workload.map(([name, count]) => (
                  <Box key={name} sx={{ display: 'grid', gridTemplateColumns: '120px 1fr 32px', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{name}</Typography>
                    <Box sx={{ height: 9, borderRadius: '20px', backgroundColor: tokens.colors.panel2, overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', borderRadius: '20px', width: `${(count / maxWorkload) * 100}%`, backgroundImage: tokens.gradient.main }} />
                    </Box>
                    <Typography sx={{ fontSize: 12.5, color: tokens.colors.muted, fontWeight: 650, textAlign: 'right' }}>{count}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={cardSx}>
                <Typography sx={{ fontSize: 15, fontWeight: 680 }}>Actividad reciente</Typography>
                <Typography sx={{ fontSize: 12.5, color: tokens.colors.muted, mb: 2 }}>Últimos cambios en expedientes</Typography>
                <Typography sx={{ color: tokens.colors.muted, fontSize: 13, textAlign: 'center', py: 2 }}>
                  El registro de actividad se habilitará próximamente.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* ---------- Encabezado de Expedientes ---------- */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
            <Box>
              <Typography sx={{ fontSize: 26, fontWeight: 730, letterSpacing: '-.4px' }}>
                Expedientes
              </Typography>
              <Typography sx={{ color: tokens.colors.muted, mt: 0.5, fontSize: 14 }}>
                {filteredExpedients.length} expedientes · {office?.name}
              </Typography>
            </Box>

            {userRole === 'admin' && (
              <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx} onClick={handleCreateExpedient}>
                Nuevo expediente
              </Button>
            )}
          </Box>

          {/* ---------- Toolbar: tabs + chips de área ---------- */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                backgroundColor: tokens.colors.panel,
                border: `1px solid ${tokens.colors.line}`,
                borderRadius: `${tokens.radius.md}px`,
                p: 0.5,
              }}
            >
              {CASE_TABS.map(tab => (
                <Box
                  key={tab.value}
                  onClick={() => setCaseTab(tab.value)}
                  sx={{
                    px: 1.75, py: 0.75, borderRadius: `${tokens.radius.sm}px`,
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    color: caseTab === tab.value ? tokens.colors.txt : tokens.colors.muted,
                    backgroundColor: caseTab === tab.value ? tokens.colors.panel2 : 'transparent',
                  }}
                >
                  {tab.label}
                </Box>
              ))}
            </Box>

            <FilterChip label="Todas las áreas" active={selectedType === ''} onClick={() => setSelectedType('')} />
            {loadingPracticeAreas ? (
              <>
                <Skeleton variant="rounded" width={100} height={34} sx={{ borderRadius: `${tokens.radius.md}px` }} />
                <Skeleton variant="rounded" width={90} height={34} sx={{ borderRadius: `${tokens.radius.md}px` }} />
              </>
            ) : (
              practiceAreas.map(area => (
                <FilterChip
                  key={area.id}
                  label={area.name}
                  active={selectedType === area.id}
                  onClick={() => setSelectedType(selectedType === area.id ? '' : area.id)}
                />
              ))
            )}
          </Box>

          {/* ---------- Título de sección + toggle grid/tabla ---------- */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography sx={{ fontSize: 19, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon sx={{ color: userRole === 'admin' ? office.color : tokens.colors.txt }} />
                {userRole === 'admin' ? 'Todos los Expedientes' : 'Mis Expedientes Asignados'}
              </Typography>
              {userRole !== 'admin' && (
                <Chip
                  icon={<PersonIcon />}
                  label={`${filteredExpedients.length} expedientes`}
                  sx={{ backgroundImage: tokens.gradient.soft, color: tokens.colors.txt, border: `1px solid ${tokens.colors.line2}` }}
                />
              )}
              {userRole !== 'admin' && (
                <Chip
                  icon={<BadgeIcon />}
                  label={`Vista: ${getRoleLabel(userRole)}`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: tokens.colors.line2, color: tokens.colors.muted }}
                />
              )}
            </Box>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, value) => value && setViewMode(value)}
              size="small"
              sx={{
                backgroundColor: tokens.colors.panel,
                border: `1px solid ${tokens.colors.line}`,
                borderRadius: '12px',
                p: 0.5,
                '& .MuiToggleButton-root': { border: 'none', borderRadius: '9px !important' },
                '& .Mui-selected': { backgroundColor: `${tokens.colors.panel2} !important` },
              }}
            >
              <ToggleButton value="grid"><GridViewIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="table"><TableRowsIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* ---------- Lista de expedientes ---------- */}
          {filterLoading ? (
            <Grid container spacing={3}>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={i}>
                  <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: '18px' }} />
                </Grid>
              ))}
            </Grid>
          ) : filteredExpedients.length === 0 ? (
            <Box sx={{ ...cardSx, p: 6, textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 80, color: tokens.colors.muted2, mb: 2 }} />
              <Typography sx={{ fontWeight: 680, mb: 1 }}>No se encontraron expedientes</Typography>
              <Typography sx={{ color: tokens.colors.muted, fontSize: 13.5, mb: 3 }}>
                {selectedType || caseTab !== 'todos'
                  ? 'Intenta ajustar los filtros'
                  : userRole === 'admin' ? 'No hay expedientes en este despacho' : 'No tienes expedientes asignados en este momento'}
              </Typography>
              {userRole === 'admin' && !selectedType && caseTab === 'todos' && (
                <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx} onClick={handleCreateExpedient} size="large">
                  Crear Primer Expediente
                </Button>
              )}
            </Box>
          ) : viewMode === 'grid' ? (
            <Grid container spacing={3}>
              {filteredExpedients.map(expedient => (
                <CaseCard
                  onViewDetails={() => navigate(`/office/${office.id}/case/${expedient.id}`)}
                  key={expedient.id}
                  expedient={expedient}
                  userRoleInCase={expedient.userRoleInCase}
                  showRoleBadge={userRole !== 'admin'}
                  officeColor={office?.color}
                />
              ))}
            </Grid>
          ) : (
            <CaseTable
              expedients={filteredExpedients}
              onViewDetails={(id) => navigate(`/office/${office.id}/case/${id}`)}
              officeColor={office?.color}
            />
          )}

          {!isInitialLoading && !filterLoading && filteredExpedients.length > 0 && (
            <Box mt={4} textAlign="center">
              <Typography variant="body2" sx={{ color: tokens.colors.muted }}>
                {userRole === 'admin'
                  ? `Mostrando ${filteredExpedients.length} de ${expedients.length} expedientes`
                  : `Mostrando ${filteredExpedients.length} expedientes asignados`}
              </Typography>
            </Box>
          )}
        </>
    </Container>
  );
};

export default OfficeHomeView;